"use client";

import React, { useState, useRef, useEffect, Fragment } from 'react';

// --- C2PA Category helpers ---
const C2PA_CATEGORY_CONFIG: Record<string, { label: string; color: string; bg: string; icon: string; description: string }> = {
  camera_capture: {
    label: "Zajem kamere",
    color: "#4ade80",
    bg: "rgba(74, 222, 128, 0.12)",
    icon: "📷",
    description: "C2PA manifest izvira iz fotoaparata. Kasnejše ureditve niso zabeležene v verigi.",
  },
  camera_edited: {
    label: "Zajem + ureditev",
    color: "#60a5fa",
    bg: "rgba(96, 165, 250, 0.12)",
    icon: "📷✏️",
    description: "C2PA manifest izvira iz fotoaparata. Naknadna ureditev je zabeležena v verigi.",
  },
  ai_generated: {
    label: "AI generacija",
    color: "#fb923c",
    bg: "rgba(251, 146, 60, 0.12)",
    icon: "🤖",
    description: "C2PA manifest kaže, da je bila slika generirana z AI orodjem.",
  },
  ai_edited: {
    label: "AI ureditev",
    color: "#fb923c",
    bg: "rgba(251, 146, 60, 0.12)",
    icon: "🤖✏️",
    description: "C2PA manifest kaže, da je bila slika urejena z AI orodjem.",
  },
  software_edited: {
    label: "Programska ureditev",
    color: "#60a5fa",
    bg: "rgba(96, 165, 250, 0.12)",
    icon: "✏️",
    description: "C2PA manifest kaže, da je bila slika urejena s programsko opremo.",
  },
  unknown_valid: {
    label: "Veljaven (neznan vir)",
    color: "#a78bfa",
    bg: "rgba(167, 139, 250, 0.12)",
    icon: "✅",
    description: "C2PA manifest je kriptografsko veljaven, vendar vir ni bil prepoznan.",
  },
  invalid: {
    label: "Neveljaven",
    color: "#f87171",
    bg: "rgba(248, 113, 113, 0.12)",
    icon: "🔴",
    description: "C2PA manifest je prisoten, vendar kriptografska verifikacija ni uspela.",
  },
  absent: {
    label: "Ni prisoten",
    color: "#6b7280",
    bg: "rgba(107, 114, 128, 0.12)",
    icon: "⚪",
    description: "Slika ne vsebuje C2PA manifesta.",
  },
};

const MANIFEST_TYPE_CONFIG: Record<string, { icon: string; label: string; color: string }> = {
  capture:         { icon: "📷", label: "Zajem",            color: "#4ade80" },
  ai_generated:    { icon: "🤖", label: "AI generacija",    color: "#fb923c" },
  software_editing:{ icon: "✏️", label: "Ureditev",         color: "#60a5fa" },
  unknown:         { icon: "❓", label: "Neznan",            color: "#9ca3af" },
};

// ─── Consistency Matrix ──────────────────────────────────────────────────────

// Vrednosti celice
type CellValue = 'yes' | 'no' | 'warn' | 'na';

interface MatrixCol {
  key: string;
  label: string;
  shortLabel: string;
  description: string;
  getValue: (r: any, sourceGroup?: string) => { value: CellValue; tooltip: string };
}

const MATRIX_COLS: MatrixCol[] = [
  {
    key: 'exif', label: 'EXIF Metapodatki', shortLabel: 'EXIF',
    description: 'Metapodatki, ki jih ob zajemu zapiše fotoaparat ali naprava. Vsebujejo model naprave, datum, GPS koordinate in tehnične nastavitve.\n✅ Prisotni: slika vsebuje sledljive metapodatke naprave.\n❌ Odsotni: slika je bila ustvarjena brez naprave ali so bili metapodatki odstranjeni.',
    getValue: (r) => {
      const e = r.analysis_elements?.exif;
      if (!e) return { value: 'na', tooltip: 'Ni podatkov' };
      if (!e.present) return { value: 'no', tooltip: 'EXIF metapodatki niso prisotni' };
      const parts = [];
      if (e.has_camera_model) parts.push('model naprave');
      if (e.has_datetime) parts.push('datum');
      if (e.has_gps) parts.push('GPS');
      return { value: 'yes', tooltip: `Prisotnih ${e.field_count} polj${parts.length ? ': ' + parts.join(', ') : ''}` };
    },
  },
  {
    key: 'c2pa', label: 'C2PA Certifikat', shortLabel: 'C2PA',
    description: 'Kriptografski standard za dokazovanje porekla vsebine (Content Credentials). Podpišejo ga fotoaparati, AI orodja ali programi, ki to podpirajo.\n✅ Prisoten in veljaven: slika ima digitalni podpis o poreklu.\n⚠️ Neveljaven: certifikat je bil najden, a kriptografska preveritev ni uspela.\n❌ Odsoten: slika nima certifikata — to je normalno za večino fotografij.',
    getValue: (r) => {
      const c = r.analysis_elements?.c2pa;
      if (!c) return { value: 'na', tooltip: 'Ni podatkov' };
      if (!c.present) return { value: 'no', tooltip: c.message || 'C2PA manifest ni prisoten' };
      if (!c.valid) return { value: 'warn', tooltip: c.message || 'C2PA manifest je prisoten, a kriptografska verifikacija ni uspela' };
      return { value: 'yes', tooltip: c.generator ? `Prisoten: ${c.generator}` : `Prisoten — kategorija: ${c.category}` };
    },
  },
  {
    key: 'xmp', label: 'XMP Metapodatki', shortLabel: 'XMP',
    description: 'Adobe-ov metapodatkovni standard, ki ga zapišejo orodja kot so Lightroom, Photoshop, Capture One. Polje CreatorTool razkrije, katero orodje je datoteko nazadnje shranilo.\n✅ Prisotni: slika je bila obdelana ali shranjena z orodjem, ki podpira XMP.\n❌ Odsotni: slika ni bila obdelana z Adobe orodji ali je bil XMP odstranjen.',
    getValue: (r) => {
      const x = r.analysis_elements?.extended?.xmp;
      if (!x) return { value: 'na', tooltip: 'Ni podatkov' };
      if (!x.present) return { value: 'no', tooltip: 'XMP metapodatki niso prisotni' };
      return { value: 'yes', tooltip: x.creator_tool ? `CreatorTool: ${x.creator_tool}` : 'XMP prisoten (orodje ni prepoznano)' };
    },
  },
  {
    key: 'icc', label: 'ICC Barvni profil', shortLabel: 'ICC',
    description: 'Barvni profil, vgrajen v datoteko (npr. sRGB, Adobe RGB). Kamere in orodja za obdelavo slik ga vedno vgradijo.\n✅ Prisoten: slika vsebuje barvni profil (tipično za izvorne posnetke).\n❌ Odsoten: barvni profil manjka — nenavadno za izvorno fotografijo, pogosto pri screenshot-ih in AI-generiranih slikah.',
    getValue: (r) => {
      const icc = r.analysis_elements?.extended?.icc_profile;
      if (!icc) return { value: 'na', tooltip: 'Ni podatkov' };
      if (!icc.present) return { value: 'no', tooltip: 'ICC barvni profil ni prisoten' };
      return { value: 'yes', tooltip: `Profil: ${icc.profile_name || 'neznan'}` };
    },
  },
  {
    key: 'iptc', label: 'IPTC Metapodatki', shortLabel: 'IPTC',
    description: 'Novinarski metapodatkovni standard (International Press Telecommunications Council). Vsebuje podatke o avtorju, agenciji, naslovu in avtorskih pravicah.\n✅ Prisotni: slika je bila opremljena z novinarskimi metapodatki.\n❌ Odsotni: ni novinarskih metapodatkov (normalno za osebne fotografije).',
    getValue: (r) => {
      const ip = r.analysis_elements?.extended?.iptc;
      if (!ip) return { value: 'na', tooltip: 'Ni podatkov' };
      return ip.present
        ? { value: 'yes', tooltip: 'IPTC metapodatki so prisotni' }
        : { value: 'no',  tooltip: 'IPTC metapodatki niso prisotni' };
    },
  },
  {
    key: 'datetime', label: 'Datum/čas', shortLabel: 'Datum',
    description: 'Primerjava med datumom zajema slike (DateTimeOriginal) in datumom zadnje spremembe (EXIF DateTime in OS Datum datoteke).\n✅ Skladen: datumi se ujemajo — datoteka ni bila spremenjena po zajemu.\n⚠️ Neskladje: datumi se razlikujejo — datoteka je bila obdelana (npr. shranjena s Paintom, Photoshopom ipd).\n❌ Ni podatkov: datumski podatki niso prisotni.',
    getValue: (r) => {
      const dt = r.analysis_elements?.extended?.datetime_consistency;
      if (!dt) return { value: 'na', tooltip: 'Ni podatkov' };
      if (!dt.has_original) return { value: 'no', tooltip: 'Podatki o datumu niso prisotni' };
      
      const lines = [];
      if (dt.original) lines.push(`Zajeto (Original): ${dt.original}`);
      if (dt.modified) lines.push(`Spremenjeno (EXIF): ${dt.modified}`);
      if (dt.os_modified) lines.push(`Spremenjeno (OS): ${dt.os_modified}`);
      
      if (dt.mismatch === true) {
         return { value: 'warn', tooltip: `Neskladje datumov:\n${lines.join('\n')}` };
      }
      return { value: 'yes', tooltip: `Skladen:\n${lines.join('\n')}` };
    },
  },
  {
    key: 'thumbnail', label: 'EXIF Thumbnail', shortLabel: 'Thumb',
    description: 'Miniaturna slika, vgrajena v EXIF podatke. Fotoaparati jo ustvarijo ob zajemu slike.\n✅ Prisotna: kamera je vgradila miniaturo (značilno za izvorne posnetke).\n❌ Odsotna: miniatura ni prisotna — pogosto pri screenshot-ih, AI-generiranih slikah ali po agresivni obdelavi.',
    getValue: (r) => {
      const t = r.analysis_elements?.extended?.thumbnail;
      if (!t) return { value: 'na', tooltip: 'Ni podatkov' };
      return t.present
        ? { value: 'yes', tooltip: 'EXIF thumbnail je prisoten' }
        : { value: 'no',  tooltip: 'EXIF thumbnail ni prisoten' };
    },
  },
  {
    key: 'software', label: 'Software', shortLabel: 'SW',
    description: 'Polje v EXIF metapodatkih, ki zabeleži ime programske opreme, ki je datoteko nazadnje zapisala.\n✅ Prisotno: ime orodja je razvidno (npr. "Adobe Photoshop", "Google", "HDR-Art").\n❌ Odsotno: programska oprema se ni zabeležila ali je bilo polje odstranjeno.',
    getValue: (r) => {
      const sw = r.analysis_elements?.extended?.software;
      if (!sw) return { value: 'na', tooltip: 'Ni podatkov' };
      if (!sw.present) return { value: 'no', tooltip: 'Software polje ni prisotno' };
      return { value: 'yes', tooltip: sw.value || 'Software prisoten' };
    },
  },
  {
    key: 'serial', label: 'Serijska št. kamere', shortLabel: 'Serial',
    description: 'Serijska številka fotoaparata, ki jo nekateri modeli (Canon, Nikon, Sony) zapišejo v EXIF podatke.\n✅ Prisotna: fotoaparat je zabeležil svojo serijsko številko — omogoča identifikacijo naprave.\n❌ Odsotna: serijska številka ni prisotna (večina pametnih telefonov je ne zapisuje).',
    getValue: (r) => {
      const s = r.analysis_elements?.extended?.camera_serial;
      if (!s) return { value: 'na', tooltip: 'Ni podatkov' };
      return s.present
        ? { value: 'yes', tooltip: `Serijska: ${s.value}` }
        : { value: 'no',  tooltip: 'Serijska številka kamere ni prisotna' };
    },
  },
  {
    key: 'icc_exif_integrity', label: 'Integriteta ICC in EXIF', shortLabel: 'EXIF/ICC Skladnost',
    description: 'Preverja prisotnost osnovnih EXIF metapodatkov (kamera, itd.) ob zaznanem barvnem profilu.\n✅ Ustreza: Prisotna sta tako ICC profil kot EXIF metapodatki (normalno za izvirnike).\n❌ Ne ustreza: Zaznan je bil ICC profil, originalnih EXIF metapodatkov pa ni. To pogosto pomeni prenos preko spleta ali aplikacij, ki izbrišejo EXIF, a ohranijo barvni profil.\n— Ni na voljo: ICC barvni profil sploh ni vgrajen.',
    getValue: (r) => {
      const icc = r.analysis_elements?.extended?.icc_profile;
      const exif = r.analysis_elements?.exif;
      if (!icc || !icc.present) return { value: 'na', tooltip: 'ICC profil ni zaznan' };
      if (icc.present && (!exif || !exif.present)) {
        return { value: 'no', tooltip: 'Zaznan je ICC profil, manjka pa EXIF (možen prenos preko interneta)' };
      }
      return { value: 'yes', tooltip: 'Oba elementa (ICC in EXIF) sta prisotna' };
    },
  },
];

// Prikaz celice matrike
function MatrixCell({ value, tooltip, isOutlier }: { value: CellValue; tooltip: string; isOutlier: boolean }) {
  const [show, setShow] = React.useState(false);
  const iconMap: Record<CellValue, string> = { yes: '✅', no: '❌', warn: '⚠️', na: '—' };
  const colorMap: Record<CellValue, string> = {
    yes: '#4ade80', no: '#6b7280', warn: '#fbbf24', na: '#374151',
  };
  return (
    <td
      style={{
        padding: '0.6rem 0.4rem', textAlign: 'center', position: 'relative', cursor: 'default',
        borderBottom: '1px solid #1f2937',
        outline: isOutlier ? '2px solid rgba(248,113,113,0.6)' : 'none',
        outlineOffset: '-2px',
        borderRadius: isOutlier ? '4px' : undefined,
        background: isOutlier ? 'rgba(248,113,113,0.06)' : 'transparent',
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span style={{ fontSize: '1rem', color: colorMap[value] }}>{iconMap[value]}</span>
      {show && (
        <div style={{
          position: 'absolute', bottom: '110%', left: '50%', transform: 'translateX(-50%)',
          background: '#1f2937', border: '1px solid #374151', borderRadius: '6px',
          padding: '0.4rem 0.6rem', fontSize: '0.72rem', color: '#e5e7eb',
          zIndex: 50, pointerEvents: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          maxWidth: '220px', whiteSpace: 'normal', textAlign: 'left',
        }}>
          {tooltip}
          {isOutlier && <div style={{ color: '#f87171', marginTop: '0.2rem', fontWeight: 600 }}>⚠ Odstopa od večine</div>}
        </div>
      )}
    </td>
  );
}

// ─── EXIF Modal Content ───────────────────────────────────────────────────────
function ExifModalContent({ exif }: { exif: Record<string, string> }) {
  const [search, setSearch] = React.useState('');
  const entries = Object.entries(exif);
  const filtered = search.trim()
    ? entries.filter(([k, v]) =>
        k.toLowerCase().includes(search.toLowerCase()) ||
        v.toLowerCase().includes(search.toLowerCase())
      )
    : entries;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      {/* Iskanje */}
      <div style={{ padding: '0.75rem 1.5rem', borderBottom: '1px solid #1f2937', flexShrink: 0 }}>
        <input
          type="text"
          placeholder="Iskanje po polju ali vrednosti..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoFocus
          style={{
            width: '100%', padding: '0.5rem 0.75rem', background: '#0d1117',
            border: '1px solid #374151', borderRadius: '6px',
            color: '#f3f4f6', fontSize: '0.85rem', boxSizing: 'border-box',
            outline: 'none',
          }}
        />
      </div>

      {/* Tabela */}
      <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '2rem', color: '#6b7280', textAlign: 'center', fontSize: '0.85rem' }}>
            Ni zadetkov za &quot;{search}&quot;
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead style={{ position: 'sticky', top: 0, background: '#111827', zIndex: 1 }}>
              <tr style={{ borderBottom: '1px solid #374151' }}>
                <th style={{ padding: '0.5rem 1.5rem', textAlign: 'left', color: '#6b7280', fontWeight: 600, width: '40%' }}>Polje</th>
                <th style={{ padding: '0.5rem 1.5rem', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Vrednost</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(([key, val]) => (
                <tr key={key} style={{ borderBottom: '1px solid #1f2937' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ padding: '0.5rem 1.5rem', color: '#9ca3af', verticalAlign: 'top', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                    {key}
                  </td>
                  <td style={{ padding: '0.5rem 1.5rem', color: '#e5e7eb', verticalAlign: 'top', wordBreak: 'break-word' }}>
                    {val}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── EXIF Accordion ──────────────────────────────────────────────────────────
function ExifAccordion({ exif }: { exif: Record<string, string> | undefined }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  if (!exif) return null;

  const entries = Object.entries(exif);
  const filtered = search.trim()
    ? entries.filter(([k, v]) =>
        k.toLowerCase().includes(search.toLowerCase()) ||
        v.toLowerCase().includes(search.toLowerCase())
      )
    : entries;

  return (
    <div style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #1f2937', borderRadius: '6px', overflow: 'hidden', marginTop: '0.25rem' }}>
      {/* Header — klik razpre/zapre */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', background: 'rgba(255,255,255,0.02)', border: 'none',
          padding: '0.6rem 0.8rem', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          color: '#e5e7eb',
        }}
      >
        <span style={{ fontWeight: 600, fontSize: '0.8rem' }}>
          📋 Vsa EXIF polja
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.72rem', color: '#6b7280', background: 'rgba(255,255,255,0.06)', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>
            {entries.length} polj
          </span>
          <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>{open ? '▲' : '▼'}</span>
        </span>
      </button>

      {/* Razprta vsebina */}
      {open && (
        <div style={{ borderTop: '1px solid #1f2937' }}>
          {/* Iskanje */}
          <div style={{ padding: '0.5rem 0.8rem', borderBottom: '1px solid #1f2937' }}>
            <input
              type="text"
              placeholder="Iskanje po polju ali vrednosti..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
              style={{
                width: '100%', padding: '0.35rem 0.6rem', background: '#0d1117',
                border: '1px solid #374151', borderRadius: '4px',
                color: '#f3f4f6', fontSize: '0.75rem', boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Tabela */}
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.75rem', textAlign: 'center' }}>
                Ni zadetkov.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                <tbody>
                  {filtered.map(([key, val]) => (
                    <tr key={key} style={{ borderBottom: '1px solid #0d1117' }}>
                      <td style={{
                        padding: '0.3rem 0.8rem', color: '#9ca3af', verticalAlign: 'top',
                        whiteSpace: 'nowrap', width: '40%', fontFamily: 'monospace',
                      }}>
                        {key}
                      </td>
                      <td style={{
                        padding: '0.3rem 0.8rem', color: '#e5e7eb', verticalAlign: 'top',
                        wordBreak: 'break-word',
                      }}>
                        {val}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Glavna matrična komponenta
function ConsistencyMatrix({
  results, sourceGroups, sourceDefs, GROUP_LETTERS, GROUP_COLORS,
  selectedManifestChain, setSelectedManifestChain,
  onRowClick,
}: any) {
  const [showLegend, setShowLegend] = React.useState(false);
  const [selectedExif, setSelectedExif] = React.useState<{ filename: string; exif: Record<string, string> } | null>(null);

  // Izračun vrednosti večine za vsak stolpec
  const getMajority = (col: MatrixCol): CellValue => {
    const vals = results.filter((r: any) => !r.error).map((r: any, i: number) => col.getValue(r, sourceGroups[i]).value);
    const freq: Record<string, number> = {};
    vals.forEach((v: string) => { freq[v] = (freq[v] || 0) + 1; });
    return (Object.entries(freq).sort((a, b) => b[1] - a[1])[0]?.[0] || 'na') as CellValue;
  };

  const majorities = Object.fromEntries(MATRIX_COLS.map(col => [col.key, getMajority(col)]));

  // Outlier summary per image
  const outlierSummary = results.map((r: any, i: number) => {
    if (r.error) return { filename: r.filename, deviations: [], count: 0 };
    
    const devsList = MATRIX_COLS.filter(col => {
      const cell = col.getValue(r, sourceGroups[i]);
      return cell.value !== majorities[col.key] && majorities[col.key] !== 'na';
    }).map(col => {
      const cell = col.getValue(r, sourceGroups[i]);
      const maj = majorities[col.key];
      let reason = 'odstopanje';
      if (maj === 'yes' && cell.value === 'no') reason = 'odsotnost elementov';
      else if (maj === 'no' && cell.value === 'yes') reason = 'nepričakovana prisotnost';
      else if (maj === 'yes' && cell.value === 'warn') reason = 'neskladje ali manjkajoče informacije';
      else if (maj === 'yes' && cell.value === 'na') reason = 'manjkajoči podatki';
      return { label: col.shortLabel, reason };
    });

    if (devsList.length === 0) {
      return { filename: r.filename, deviations: [], count: 0 };
    }

    const uniqueReasons = [...new Set(devsList.map(d => d.reason))];
    let deviationsOutput = [];
    if (uniqueReasons.length === 1 && devsList.length > 1) {
       const labels = devsList.map(d => d.label).join(', ');
       deviationsOutput = [`${labels} (${uniqueReasons[0]})`];
    } else {
       deviationsOutput = devsList.map(d => `${d.label} (${d.reason.replace('elementov', 'elementa')})`);
    }

    return { filename: r.filename, deviations: deviationsOutput, count: devsList.length };
  }).filter((s: any) => s.count > 0);

  return (
    <>
      <div className="no-print" style={{ overflowX: 'auto' }}>

      {/* Gumb Legenda */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
        <button
          onClick={() => setShowLegend(true)}
          style={{
            fontSize: '0.78rem', padding: '0.3rem 0.8rem', cursor: 'pointer',
            background: 'rgba(99,102,241,0.12)', color: '#a5b4fc',
            border: '1px solid rgba(99,102,241,0.3)', borderRadius: '6px',
            display: 'flex', alignItems: 'center', gap: '0.35rem',
          }}
        >📖 Legenda elementov</button>
      </div>

      {/* Modalno okno Legenda */}
      {showLegend && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={() => setShowLegend(false)}
        >
          <div
            style={{ position: 'relative', width: '100%', maxWidth: '680px', maxHeight: '88vh', overflowY: 'auto', background: '#111827', borderRadius: '14px', border: '1px solid #374151', boxShadow: '0 24px 60px rgba(0,0,0,0.7)', padding: '2rem' }}
            onClick={e => e.stopPropagation()}
          >
            <button onClick={() => setShowLegend(false)} style={{ position: 'absolute', top: '1rem', right: '1rem', background: '#374151', color: '#9ca3af', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer', fontSize: '0.9rem' }}>✕</button>
            <h3 style={{ margin: '0 0 0.25rem 0', color: '#f3f4f6' }}>📖 Legenda elementov analize</h3>
            <p style={{ margin: '0 0 1.5rem 0', color: '#6b7280', fontSize: '0.83rem' }}>Pojasnilo vsakega preverjenega elementa in pomen prisotnosti oz. odsotnosti.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {MATRIX_COLS.map(col => (
                <div key={col.key} style={{ padding: '0.9rem 1rem', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid #1f2937' }}>
                  <div style={{ fontWeight: 700, color: '#e5e7eb', marginBottom: '0.4rem', fontSize: '0.9rem' }}>{col.label}</div>
                  <div style={{ fontSize: '0.82rem', color: '#9ca3af', lineHeight: 1.65, whiteSpace: 'pre-line' }}>{col.description}</div>
                </div>
              ))}
              {/* Izvor */}
              <div style={{ padding: '0.9rem 1rem', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid #1f2937' }}>
                <div style={{ fontWeight: 700, color: '#e5e7eb', marginBottom: '0.4rem', fontSize: '0.9rem' }}>Izvor</div>
                <div style={{ fontSize: '0.82rem', color: '#9ca3af', lineHeight: 1.65 }}>
                  Prstni odtis izvora, izračunan iz kombinacije EXIF polj (Make + Model + Leča + Software) in C2PA izdajatelja.<br />
                  Fotografije z enakim prstnim odtisom dobijo isto skupino (A, B, C...). Različne oznake pomenijo različne tehnične profile izvora.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', color: '#e5e7eb' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #374151', color: '#9ca3af' }}>
            <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', minWidth: '150px', fontWeight: 600 }}>Datoteka</th>
            {MATRIX_COLS.map(col => (
              <th key={col.key} style={{ padding: '0.6rem 0.4rem', textAlign: 'center', fontWeight: 500, whiteSpace: 'nowrap' }}>
                {col.shortLabel}
              </th>
            ))}
            <th style={{ padding: '0.6rem 0.4rem', textAlign: 'center', fontWeight: 500 }}>Izvor</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r: any, i: number) => {
            const ae = r.analysis_elements;
            const hasManifestChain = ae?.c2pa?.manifest_chain?.length > 0;
            const grp = sourceGroups[i];
            const colorConf = grp && grp !== '?'
              ? GROUP_COLORS[GROUP_LETTERS.indexOf(grp) % GROUP_COLORS.length]
              : null;

            return (
              <Fragment key={i}>
                <tr 
                  style={{ borderBottom: '1px solid #1f2937', cursor: 'pointer', transition: 'background-color 0.2s' }}
                  onClick={() => onRowClick && onRowClick(i)}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                   {/* Filename */}
                  <td style={{ padding: '0.6rem 0.75rem', fontWeight: 500, maxWidth: '200px', wordBreak: 'break-word' }}>
                    {r.filename}
                    <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
                      {hasManifestChain && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedManifestChain({ filename: r.filename, chain: ae.c2pa.manifest_chain }); }}
                          style={{
                            fontSize: '0.68rem', padding: '0.1rem 0.4rem',
                            cursor: 'pointer', background: 'rgba(99,102,241,0.15)', color: '#a5b4fc',
                            border: '1px solid rgba(99,102,241,0.3)', borderRadius: '3px',
                          }}
                        >📋 Manifesti</button>
                      )}
                      {r.exif && Object.keys(r.exif).length > 0 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedExif({ filename: r.filename, exif: r.exif }); }}
                          style={{
                            fontSize: '0.68rem', padding: '0.1rem 0.4rem',
                            cursor: 'pointer', background: 'rgba(74,222,128,0.12)', color: '#86efac',
                            border: '1px solid rgba(74,222,128,0.3)', borderRadius: '3px',
                          }}
                        >🔍 EXIF</button>
                      )}
                    </div>
                  </td>

                  {r.error ? (
                    <td colSpan={MATRIX_COLS.length + 1} style={{ padding: '0.6rem', color: '#f87171' }}>
                      ⚠️ {r.error}
                    </td>
                  ) : (
                    <>
                      {MATRIX_COLS.map(col => {
                        const cell = col.getValue(r, sourceGroups[i]);
                        const isOutlier = cell.value !== majorities[col.key] && majorities[col.key] !== 'na';
                        return <MatrixCell key={col.key} value={cell.value} tooltip={cell.tooltip} isOutlier={isOutlier} />;
                      })}

                      {/* Izvor */}
                      <td style={{ padding: '0.6rem 0.4rem', textAlign: 'center' }}>
                        {colorConf ? (
                          <span style={{
                            display: 'inline-flex', padding: '0.15rem 0.55rem', borderRadius: '12px',
                            background: colorConf.bg, border: `1px solid ${colorConf.border}`,
                            color: colorConf.color, fontWeight: 700, fontSize: '0.8rem',
                          }}>Vir {grp}</span>
                        ) : <span style={{ color: '#4b5563' }}>⚪</span>}
                      </td>
                    </>
                  )}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Outlier summary */}
      {outlierSummary.length > 0 && (
        <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          {outlierSummary.map((s: any, i: number) => (
            <div key={i} style={{
              padding: '0.5rem 0.75rem', background: 'rgba(248,113,113,0.07)',
              border: '1px solid rgba(248,113,113,0.2)', borderRadius: '6px',
              fontSize: '0.79rem', color: '#fca5a5',
            }}>
              ⚠️ <strong>{s.filename}</strong> se razlikuje od večine v {s.count} {s.count === 1 ? 'elementu' : 'elementih'}:{' '}
              <span style={{ color: '#f87171' }}>{s.deviations.join(', ')}</span>
            </div>
          ))}
        </div>
      )}

      {/* Source summary */}
      {(() => {
        const distinctGroups = [...new Set(Object.values(sourceGroups).filter((g: any) => g && g !== '?'))] as string[];
        const unknownCount = Object.values(sourceGroups).filter((g: any) => !g).length;
        if (distinctGroups.length === 0 && unknownCount === 0) return null;
        return (
          <div style={{
            marginTop: '0.75rem', padding: '0.8rem 1rem',
            background: 'rgba(255,255,255,0.02)', borderRadius: '6px',
            border: '1px solid #1f2937', fontSize: '0.82rem', color: '#9ca3af',
            display: 'flex', flexDirection: 'column', gap: '0.6rem'
          }}>
            <div style={{ fontWeight: 600, color: '#e5e7eb', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
               <span>🔍</span> 
               <span>Zaznani izvori ({distinctGroups.length}{unknownCount > 0 ? ` + ${unknownCount} neznanih` : ''}):</span>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginLeft: '0.5rem' }}>
              {distinctGroups.map((g: string) => {
                const c = GROUP_COLORS[GROUP_LETTERS.indexOf(g) % GROUP_COLORS.length];
                return (
                  <div key={g} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <span style={{ display: 'inline-flex', padding: '0.15rem 0.5rem', borderRadius: '12px', background: c.bg, border: `1px solid ${c.border}`, color: c.color, fontWeight: 700, fontSize: '0.78rem', minWidth: '55px', justifyContent: 'center' }}>
                      Vir {g}
                    </span>
                    <span style={{ color: '#f3f4f6' }}>{sourceDefs?.[g] || 'Neznano'}</span>
                  </div>
                );
              })}
              
              {unknownCount > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <span style={{ display: 'inline-flex', padding: '0.15rem 0.5rem', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid #4b5563', color: '#9ca3af', fontWeight: 700, fontSize: '0.78rem', minWidth: '55px', justifyContent: 'center' }}>
                    ⚪
                  </span>
                  <span style={{ color: '#9ca3af' }}>{unknownCount} {unknownCount === 1 ? 'fotografija' : unknownCount === 2 ? 'fotografiji' : 'fotografij'} z neznanim izvorom</span>
                </div>
              )}
            </div>
          </div>
        );
      })()}
      </div>
      <div className="print-only">
        <div className="print-header">
           <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '24pt' }}>Poročilo o analizi izvora fotografij</h1>
           <div style={{ fontSize: '12pt', opacity: 0.9 }}>Tehnično poročilo o stanju metapodatkov in elementih preverjanja</div>
        </div>

        <div className="print-box">
           <strong>Splošna opomba:</strong> Aplikacija ne ocenjuje verodostojnosti fotografij. Poroča izključno o prisotnosti in stanju posameznih elementov analize ter izračunava prstne odtise izvora na podlagi tehničnih parametrov.
        </div>

        <h2 className="print-section-title">1. Pregled zaznanih izvorov</h2>
        {(() => {
          const distinctGroups = [...new Set(Object.values(sourceGroups).filter((g: any) => g && g !== '?'))] as string[];
          const unknownCount = Object.values(sourceGroups).filter((g: any) => !g).length;
          return (
            <div>
              {distinctGroups.map((g: string) => {
                 const count = Object.values(sourceGroups).filter(sg => sg === g).length;
                 return (
                   <div key={g} className="print-box" style={{ marginBottom: '0.8rem' }}>
                     <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.4rem', color: '#1e3a8a' }}>Vir {g}</div>
                     <div>{sourceDefs?.[g] || 'Neznano'} | <strong>Število datotek:</strong> {count}</div>
                   </div>
                 );
              })}
              {unknownCount > 0 && (
                 <div className="print-box">
                   <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.4rem', color: '#1e3a8a' }}>Neznan izvor ( ⚪ )</div>
                   <div><strong>Število datotek:</strong> {unknownCount}</div>
                 </div>
              )}
            </div>
          );
        })()}

        <h2 className="print-section-title">2. Identificirana odstopanja</h2>
        {outlierSummary.length > 0 ? (
          <div className="print-box">
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Zabeležena odstopanja od večine vzorcev:</div>
            <ul style={{ margin: 0, paddingLeft: '1.5rem', lineHeight: 1.5 }}>
              {outlierSummary.map((s: any, i: number) => (
                <li key={i}>
                  <strong>{s.filename}</strong> se razlikuje od večine v {s.count} {s.count === 1 ? 'elementu' : 'elementih'}: {s.deviations.join(', ')}.
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="print-box">Ni zaznanih odstopanj.</div>
        )}

        <h2 className="print-section-title" style={{ pageBreakBefore: 'always' }}>3. Tabelarni pregled rezultatov analize</h2>
        <table className="print-table">
          <thead>
            <tr>
              <th>Datoteka</th>
              {MATRIX_COLS.map(col => <th key={col.key}>{col.shortLabel}</th>)}
              <th>Izvor</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r: any, i: number) => {
              if (r.error) return null;
              const grp = sourceGroups[i];
              return (
                <tr key={i}>
                  <td>{r.filename}</td>
                  {MATRIX_COLS.map(col => {
                    const cell = col.getValue(r, sourceGroups[i]);
                    const txt = cell.value === 'yes' ? '✅' : cell.value === 'no' ? '❌' : cell.value === 'warn' ? '⚠️' : '—';
                    return <td key={col.key} style={{textAlign: 'center'}}>{txt}</td>;
                  })}
                  <td style={{textAlign: 'center'}}>{grp && grp !== '?' ? `Vir ${grp}` : '⚪'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <h2 className="print-section-title">4. Definicije elementov analize</h2>
        <div className="print-box">
          {MATRIX_COLS.map(col => (
             <div key={col.key} style={{ marginBottom: '0.5rem' }}>
               <strong>{col.label}:</strong> {col.description.replace(/\n/g, ' ')}
             </div>
          ))}
          <div style={{ marginBottom: '0.5rem' }}>
             <strong>Izvor:</strong> Prstni odtis na podlagi kombinacije EXIF polj (Kamera + Orodje) in C2PA izdajatelja.
          </div>
        </div>
      </div>

      {/* EXIF Modal */}
      {selectedExif && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={() => setSelectedExif(null)}
        >
          <div
            style={{ position: 'relative', width: '100%', maxWidth: '640px', height: '85vh', background: '#111827', borderRadius: '14px', border: '1px solid #374151', boxShadow: '0 24px 60px rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #1f2937', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <h3 style={{ margin: 0, color: '#f3f4f6', fontSize: '1rem' }}>🔍 EXIF Metapodatki</h3>
                <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '0.2rem' }}>{selectedExif.filename}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', background: 'rgba(255,255,255,0.05)', padding: '0.2rem 0.5rem', borderRadius: '4px' }}>
                  {Object.keys(selectedExif.exif).length} polj
                </span>
                <button
                  onClick={() => setSelectedExif(null)}
                  style={{ background: '#374151', color: '#9ca3af', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >✕</button>
              </div>
            </div>

            {/* Search */}
            <ExifModalContent exif={selectedExif.exif} />
          </div>
        </div>
      )}
    </>
  );
}

export default function Dashboard() {

  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tokens, setTokens] = useState<number>(0);
  const [selectedManifestChain, setSelectedManifestChain] = useState<{ filename: string; chain: any[] } | null>(null);
  const [showRawJson, setShowRawJson] = useState(false);
  const [selectedRawJson, setSelectedRawJson] = useState<any>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [sourceGroups, setSourceGroups] = useState<{[key: number]: string}>({});
  const [sourceDefs, setSourceDefs] = useState<{[key: string]: string}>({});
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  // Preusmeri na /login, če ni JWT žetona; sicer naloži stanje žetonov
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const token = localStorage.getItem('ff_token');
    if (!token) {
      window.location.replace('/login');
      return;
    }
    fetch('/api/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      if (res.status === 401) {
        localStorage.removeItem('ff_token');
        localStorage.removeItem('ff_email');
        window.location.replace('/login');
      } else {
        return res.json();
      }
    }).then(data => {
      if (data?.tokens_balance !== undefined) setTokens(data.tokens_balance);
    }).catch(() => {});
  }, []);

  const getAuthHeader = (): Record<string, string> => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('ff_token') : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length > 0) {
      setFiles(Array.from(e.dataTransfer.files));
      setResults([]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFiles(Array.from(e.target.files));
      setResults([]);
    }
  };

  const analyzeImages = async () => {
    if (files.length === 0) return;
    setLoading(true);
    setError(null);
    setResults([]);
    const newResults: any[] = [];
    let currentTokens = tokens;
    abortControllerRef.current = new AbortController();
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        
        // Zajememo datum spremembe datoteke in ga pošljemo v EXIF formatu "YYYY:MM:DD HH:MM:SS"
        const d = new Date(file.lastModified);
        const pad = (n: number) => n.toString().padStart(2, '0');
        const osDateStr = `${d.getFullYear()}:${pad(d.getMonth() + 1)}:${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        formData.append("file_last_modified", osDateStr);

        const res = await fetch("/api/scan", {
          method: "POST",
          headers: getAuthHeader(),
          body: formData,
          signal: abortControllerRef.current?.signal,
        });
        const data = await res.json();
        if (res.status === 401) {
          localStorage.removeItem('ff_token');
          localStorage.removeItem('ff_email');
          window.location.replace('/login');
          return;
        }
        if (!res.ok) {
          newResults.push({ filename: file.name, error: data.detail || "Napaka pri analizi" });
          continue;
        }
        data.filename = file.name;
        newResults.push(data);
        currentTokens = data.tokens_remaining;
        setResults([...newResults]);
        setTokens(currentTokens);
        const { groups, defs } = computeSourceGroups(newResults);
        setSourceGroups(groups);
        setSourceDefs(defs);

      }
    } catch (err: any) {
      if (err.name === 'AbortError') setError("Analiza je bila preklicana.");
      else setError(err.message);
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  const cancelAnalysis = () => abortControllerRef.current?.abort();

  const clearResults = () => { setFiles([]); setResults([]); setError(null); setSourceGroups({}); setSourceDefs({}); };

  // --- Source origin grouping ---
  // Prstni odtis izvora: kombinacija EXIF polj naprave + programske opreme + C2PA vydajatelja
  const GROUP_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const GROUP_COLORS = [
    { color: '#60a5fa', bg: 'rgba(96,165,250,0.15)',  border: 'rgba(96,165,250,0.35)'  }, // A – modra
    { color: '#4ade80', bg: 'rgba(74,222,128,0.15)',  border: 'rgba(74,222,128,0.35)'  }, // B – zelena
    { color: '#c084fc', bg: 'rgba(192,132,252,0.15)', border: 'rgba(192,132,252,0.35)' }, // C – vijolična
    { color: '#fb923c', bg: 'rgba(251,146,60,0.15)',  border: 'rgba(251,146,60,0.35)'  }, // D – oranžna
    { color: '#f472b6', bg: 'rgba(244,114,182,0.15)', border: 'rgba(244,114,182,0.35)' }, // E – roza
    { color: '#34d399', bg: 'rgba(52,211,153,0.15)',  border: 'rgba(52,211,153,0.35)'  }, // F – menta
    { color: '#fbbf24', bg: 'rgba(251,191,36,0.15)',  border: 'rgba(251,191,36,0.35)'  }, // G – rumena
    { color: '#f87171', bg: 'rgba(248,113,113,0.15)', border: 'rgba(248,113,113,0.35)' }, // H – rdeča
  ];

  const getSourceFingerprint = (result: any): string => {
    const exif = result.exif || {};
    const make    = (exif['Image Make']    || '').trim().toLowerCase();
    const model   = (exif['Image Model']   || '').trim().toLowerCase();
    const lens    = (exif['EXIF LensModel'] || '').trim().toLowerCase();
    const sw      = (exif['Image Software'] || '').trim().toLowerCase();
    // C2PA: vzamemo generator prvega manifesta v verigi (najstarejši zapis)
    const chain   = result.c2pa?.manifest_chain || [];
    const issuer  = chain.length > 0 ? (chain[0]?.generator || '').trim().toLowerCase() : '';
    const fp = [make, model, lens, sw, issuer].join('|');
    // Prstni odtis je smiseln samo če vsebuje vsaj model kamere ali software
    if (!make && !model && !sw) return '';
    return fp;
  };

  const getSourceDefinition = (result: any): string => {
    const exif = result.exif || {};
    const make    = (exif['Image Make']    || '').trim();
    const model   = (exif['Image Model']   || '').trim();
    const sw      = (exif['Image Software'] || '').trim();
    const chain   = result.c2pa?.manifest_chain || [];
    const issuer  = chain.length > 0 ? (chain[0]?.generator || '').trim() : '';

    const parts = [];
    if (make || model) parts.push(`Kamera: ${make} ${model}`.trim());
    if (sw) parts.push(`Program: ${sw}`);
    if (issuer) parts.push(`C2PA Izdajatelj: ${issuer}`);
    
    if (parts.length === 0) return "Neznan izvor";
    return parts.join(' | ');
  };

  const computeSourceGroups = (currentResults: any[]) => {
    const fpToLetter: Record<string, string> = {};
    const groups: {[key: number]: string} = {};
    const defs: {[key: string]: string} = {};
    let nextIdx = 0;
    currentResults.forEach((r, i) => {
      if (r.error) { groups[i] = '?'; return; }
      const fp = getSourceFingerprint(r);
      if (!fp) { groups[i] = ''; return; } // prazen = neznan
      if (!(fp in fpToLetter)) {
        const letter = GROUP_LETTERS[nextIdx % 26];
        fpToLetter[fp] = letter;
        defs[letter] = getSourceDefinition(r);
        nextIdx++;
      }
      groups[i] = fpToLetter[fp];
    });
    return { groups, defs };
  };


  // --- Neutral element helpers ---
  const getExifSummary = (ae: any) => {
    const exif = ae?.exif;
    if (!exif?.present) return { present: false, label: "Ni prisoten", detail: "" };
    const parts = [];
    if (exif.has_camera_model) parts.push("model naprave");
    if (exif.has_datetime) parts.push("datum");
    if (exif.has_gps) parts.push("GPS");
    return {
      present: true,
      label: `Prisoten (${exif.field_count} polj)`,
      detail: parts.length > 0 ? `Vsebuje: ${parts.join(", ")}` : "",
    };
  };

  const getC2paSummary = (ae: any) => {
    const c2pa = ae?.c2pa;
    if (!c2pa) return C2PA_CATEGORY_CONFIG["absent"];
    return C2PA_CATEGORY_CONFIG[c2pa.category] || C2PA_CATEGORY_CONFIG["absent"];
  };

  const getOverallStatus = (ae: any) => {
    if (!ae) return { allPresent: false, label: "Podatki niso na voljo" };
    const exifOk = ae.exif?.present === true;
    const c2paOk = ae.c2pa?.present === true;
    const allPresent = exifOk && c2paOk;
    return {
      allPresent,
      label: allPresent
        ? "Vsi preverjeni elementi so prisotni"
        : "Nekateri elementi niso prisotni",
    };
  };


  return (
    <main className="container animate-fade-in">
      {/* Header */}
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: '1.5rem'}}>
          <h2 style={{margin: 0}}>Analiza slik</h2>
          {results.length > 0 && (
            <button 
              onClick={clearResults} 
              style={{
                padding: '0.45rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer',
                background: 'rgba(248,113,113,0.08)', color: '#f87171',
                border: '1px solid rgba(248,113,113,0.3)', borderRadius: '6px',
                display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: 600,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(248,113,113,0.15)'; e.currentTarget.style.transform = 'scale(1.02)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(248,113,113,0.08)'; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              🗑️ Počisti rezultate
            </button>
          )}
          {results.length > 0 && (
            <button 
              onClick={() => window.print()} 
              style={{
                padding: '0.45rem 0.9rem', fontSize: '0.8rem', cursor: 'pointer',
                background: 'rgba(96,165,250,0.08)', color: '#60a5fa',
                border: '1px solid rgba(96,165,250,0.3)', borderRadius: '6px',
                display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: 600,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(96,165,250,0.15)'; e.currentTarget.style.transform = 'scale(1.02)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(96,165,250,0.08)'; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              🖨️ Izvozi PDF
            </button>
          )}
        </div>
        <div className="token-badge">
          <span>🪙</span> {tokens} Žetonov
        </div>
      </div>

      <div className="dashboard-grid" style={{ gridTemplateColumns: '1fr' }}>
        <div className="glass-card no-print">
          <h3>Naložite fotografije</h3>
          <p style={{color: '#a0a0a0', marginBottom: '1.5rem'}}>
            Izberite eno ali več fotografij. Za vsako se preverita EXIF metapodatki in C2PA certifikat. Cena: 1 žeton na fotografijo.
          </p>
          <label className="drop-zone" onDragOver={handleDragOver} onDrop={handleDrop} style={{ display: 'block' }}>
            <input type="file" multiple accept="image/*" style={{display: 'none'}} onChange={handleFileChange} />
            <div style={{fontSize: '3rem', marginBottom: '1rem'}}>📸</div>
            <h4 style={{margin: '0'}}>
              {files.length > 0 ? `Izbranih datotek: ${files.length}` : "Povlecite datoteke sem ali kliknite za izbiro"}
            </h4>
            {!files.length && <p>Podprti formati: JPG, PNG, WEBP, HEIC</p>}
            {files.length > 0 && (
              <div style={{ marginTop: '1rem', color: '#888', fontSize: '0.9rem' }}>
                {files.map(f => f.name).join(', ')}
              </div>
            )}
          </label>
          <div style={{display: 'flex', gap: '1rem', marginTop: '1.5rem'}}>
            <button className="btn-primary" style={{flex: 1}} onClick={analyzeImages} disabled={files.length === 0 || loading}>
              {loading ? <div className="spinner"></div> : "Izvedi pregled fotografij"}
            </button>
            {loading && (
              <button onClick={cancelAnalysis} style={{
                padding: '0.75rem 1.5rem', borderRadius: '8px', border: '1px solid #f87171',
                color: '#f87171', background: 'rgba(248, 113, 113, 0.1)', cursor: 'pointer', fontWeight: 600,
              }}>Prekini</button>
            )}
          </div>
          {error && (
            <div style={{marginTop: '1rem', color: '#f87171', padding: '1rem', background: 'rgba(248, 113, 113, 0.1)', borderRadius: '8px', border: '1px solid rgba(248, 113, 113, 0.2)'}}>
              ⚠️ {error}
            </div>
          )}
        </div>

        {/* Results */}
        <div className="glass-card delay-1">
          <h3>Rezultati analize</h3>
          {/* Philosophy note */}
          <div style={{
            padding: '0.75rem 1rem', marginBottom: '1rem',
            background: 'rgba(99, 102, 241, 0.08)', borderRadius: '8px',
            border: '1px solid rgba(99, 102, 241, 0.2)', fontSize: '0.82rem', color: '#a5b4fc',
            lineHeight: 1.5,
          }}>
            ℹ️ <strong>Opomba:</strong> Aplikacija ne ocenjuje verodostojnosti fotografij. Poroča le o prisotnosti in stanju posameznih elementov analize.
          </div>

          {results.length === 0 && !loading && (
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#a0a0a0', textAlign: 'center'}}>
              <span style={{fontSize: '2rem', marginBottom: '1rem'}}>📋</span>
              Tukaj se bodo prikazali rezultati analiziranih fotografij.
            </div>
          )}

          {/* Spinner pred prvim rezultatom */}
          {loading && results.length === 0 && (
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '150px', color: '#a0a0a0'}}>
              <div className="spinner" style={{borderColor: 'rgba(99,102,241,0.3)', borderTopColor: '#6366f1', margin: '0 auto 1rem auto'}}></div>
              Analiziram fotografije...
            </div>
          )}

          {/* Matrica konsistentnosti — vedno, za vsako število slik */}
          {results.length > 0 && (
            <div className="animate-fade-in" style={{marginTop: '1rem'}}>
              {loading && (
                <div style={{marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: 'rgba(99,102,241,0.08)', borderRadius: '6px', fontSize: '0.8rem', color: '#a5b4fc', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <div className="spinner" style={{width: '14px', height: '14px', borderWidth: '2px', borderColor: 'rgba(99,102,241,0.3)', borderTopColor: '#6366f1'}}></div>
                  Analiziram preostale fotografije...
                </div>
              )}
              <ConsistencyMatrix
                results={results}
                sourceGroups={sourceGroups}
                sourceDefs={sourceDefs}
                GROUP_LETTERS={GROUP_LETTERS}
                GROUP_COLORS={GROUP_COLORS}
                selectedManifestChain={selectedManifestChain}
                setSelectedManifestChain={setSelectedManifestChain}
                onRowClick={(idx: number) => setSelectedImageIndex(idx)}
              />
            </div>
          )}

        </div>
      </div>

      {/* Manifest Chain Modal */}
      {selectedManifestChain && (
        <div
          style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem'}}
          onClick={() => { setSelectedManifestChain(null); setShowRawJson(false); }}
        >
          <div
            style={{
              position: 'relative', width: '100%', maxWidth: '640px', maxHeight: '90vh',
              overflowY: 'auto', padding: '2rem', backgroundColor: '#111827',
              borderRadius: '16px', border: '1px solid #374151', boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => { setSelectedManifestChain(null); setShowRawJson(false); }}
              style={{position: 'absolute', top: '1rem', right: '1rem', background: '#374151', color: '#9ca3af',
                border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer',
                fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
            >✕</button>

            <h3 style={{margin: '0 0 0.25rem 0', color: '#f3f4f6'}}>📋 Veriga manifestov C2PA</h3>
            <p style={{margin: '0 0 1.5rem 0', color: '#6b7280', fontSize: '0.85rem'}}>
              {selectedManifestChain.filename}
            </p>

            {/* Timeline */}
            <div style={{display: 'flex', flexDirection: 'column', gap: '0'}}>
              {selectedManifestChain.chain.map((step: any, idx: number) => {
                const typeConf = MANIFEST_TYPE_CONFIG[step.type] || MANIFEST_TYPE_CONFIG["unknown"];
                const isLast = idx === selectedManifestChain.chain.length - 1;
                return (
                  <div key={idx} style={{display: 'flex', gap: '1rem'}}>
                    {/* Timeline line */}
                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '40px'}}>
                      <div style={{
                        width: '36px', height: '36px', borderRadius: '50%',
                        background: `${typeConf.color}20`,
                        border: `2px solid ${typeConf.color}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '1rem', flexShrink: 0,
                      }}>{typeConf.icon}</div>
                      {!isLast && (
                        <div style={{width: '2px', flex: 1, minHeight: '20px', background: '#374151', margin: '4px 0'}}></div>
                      )}
                    </div>

                    {/* Step content */}
                    <div style={{
                      flex: 1, paddingBottom: isLast ? '0' : '0.5rem',
                      padding: '0.75rem 1rem', background: step.is_active ? 'rgba(99,102,241,0.08)' : 'rgba(255,255,255,0.03)',
                      borderRadius: '10px', border: `1px solid ${step.is_active ? 'rgba(99,102,241,0.3)' : '#1f2937'}`,
                      marginBottom: isLast ? '0' : '0.75rem',
                    }}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem'}}>
                        <div>
                          <span style={{fontSize: '0.75rem', color: typeConf.color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                            Korak {step.index} — {typeConf.label}
                          </span>
                          <div style={{color: '#f3f4f6', fontWeight: 600, fontSize: '0.95rem', marginTop: '0.1rem'}}>
                            {step.generator}
                          </div>
                        </div>
                        {step.is_active && (
                          <span style={{fontSize: '0.7rem', padding: '0.2rem 0.5rem', background: 'rgba(99,102,241,0.2)', color: '#a5b4fc', borderRadius: '4px', border: '1px solid rgba(99,102,241,0.3)'}}>
                            Aktiven
                          </span>
                        )}
                      </div>
                      <p style={{margin: '0 0 0.5rem 0', color: '#9ca3af', fontSize: '0.85rem', lineHeight: 1.5}}>
                        {step.explanation}
                      </p>
                      {step.timestamp && (
                        <div style={{fontSize: '0.78rem', color: '#6b7280'}}>
                          🕐 {step.timestamp}
                        </div>
                      )}
                      {step.actions?.length > 0 && (
                        <div style={{marginTop: '0.4rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap'}}>
                          {step.actions.map((a: string, ai: number) => (
                            <span key={ai} style={{fontSize: '0.72rem', padding: '0.15rem 0.4rem', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', color: '#9ca3af', border: '1px solid #374151'}}>
                              {a}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{marginTop: '1.25rem', padding: '0.75rem 1rem', background: 'rgba(107,114,128,0.1)', borderRadius: '8px', border: '1px solid #374151'}}>
              <p style={{margin: 0, fontSize: '0.78rem', color: '#6b7280', lineHeight: 1.5}}>
                ⚠️ <strong style={{color: '#9ca3af'}}>Opomba:</strong> Odsotnost C2PA manifesta ne pomeni nujno manipulacije. Veriga C2PA je prisotna le pri napravah in programih, ki ta standard podpirajo.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ČISTI MODAL - SIMPLE DESIGN */}
      {selectedImageIndex !== null && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1100,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          onClick={() => setSelectedImageIndex(null)}
        >
          <div
            style={{
              width: '85vw', maxWidth: '1400px', height: '80vh', maxHeight: '900px', background: '#000', borderRadius: '8px', overflow: 'hidden',
              display: 'flex', flexDirection: 'row', boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
              position: 'relative', border: '1px solid #374151',
              animation: 'fadeIn 0.2s ease-out'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Plavajoči gumb za zapiranje */}
            <button 
               onClick={() => setSelectedImageIndex(null)} 
               style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', zIndex: 20, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', fontSize: '1rem', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
            >✕</button>

            {/* LEVA STRAN: Slika v čvrstem flex kontejnerju */}
            <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {files[selectedImageIndex] && (
                <img 
                  src={URL.createObjectURL(files[selectedImageIndex])} 
                  alt="Preview" 
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                />
              )}
            </div>

            {/* DESNA STRAN: Rezultati (fiksna širina, višina sledi sliki) */}
            <div style={{ width: '380px', minWidth: '380px', flexShrink: 0, background: '#111827', borderLeft: '1px solid #374151', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', height: '100%', maxHeight: '100%', overflow: 'hidden' }}>
               <div style={{ padding: '1rem', borderBottom: '1px solid #1f2937', boxSizing: 'border-box', flexShrink: 0 }}>
                 <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                   {files[selectedImageIndex]?.name}
                 </div>
                 <h4 style={{ margin: 0, color: '#a5b4fc', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rezultati analize</h4>
               </div>
               
               <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem', boxSizing: 'border-box' }}>
                 {MATRIX_COLS.map(col => {
                    const r = results[selectedImageIndex];
                    const cell = col.getValue(r, sourceGroups[selectedImageIndex]);
                    const iconMap: Record<string, string> = { yes: '✅', no: '❌', warn: '⚠️', na: '—' };
                    const colorMap: Record<string, string> = { yes: '#4ade80', no: '#6b7280', warn: '#fbbf24', na: '#374151' };
                    return (
                      <div key={col.key} style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.02)', padding: '0.6rem 0.8rem', borderRadius: '6px', border: '1px solid #1f2937' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.2rem' }}>
                          <span style={{ fontWeight: 600, color: '#e5e7eb', fontSize: '0.8rem' }}>{col.label}</span>
                          <span style={{ fontSize: '1rem', color: colorMap[cell.value], marginLeft: '0.5rem', flexShrink: 0 }}>{iconMap[cell.value]}</span>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
                          {cell.tooltip}
                        </div>
                      </div>
                    );
                 })}
                 {/* EXIF Accordion */}
                 <ExifAccordion exif={results[selectedImageIndex]?.exif} />
               </div>
            </div>
          </div>
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; transform: scale(0.98); }
              to { opacity: 1; transform: scale(1); }
            }
          `}</style>
        </div>
      )}
    </main>
  );
}
